import { useBooleanState } from "@gettactic/helpers";
import { cn } from "@gettactic/helpers/src/css/cn";
import { ChevronDown, X } from "lucide-react";
import React from "react";
import { IconFromSet } from "../../../../icons";
import { Button } from "../../../../primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../../../../primitives/dropdown-menu";
import { useDataMan } from "../../hooks/useDataMan";
import { useGetDataManCallbackOrTypeValue } from "../../hooks/useGetDataManCallbackOrTypeValue";
import {
  DataManAction,
  DataManActionCallbackOptions
} from "../../types/actions";

interface BulkActionButtonProps {
  action: DataManAction;
  targetRecords: unknown[];
}

interface BulkSubActionItemProps {
  action: DataManAction;
  /** The parent action's options; the sub-action acts on the same records. */
  parentOptions: DataManActionCallbackOptions;
  onRun: (
    action: DataManAction,
    options: DataManActionCallbackOptions
  ) => Promise<void>;
}

/** One choice inside a bulk action's dropdown (an action with `subs`). */
const BulkSubActionItem: React.FC<BulkSubActionItemProps> = ({
  action,
  parentOptions,
  onRun
}) => {
  const actionOptions: DataManActionCallbackOptions = {
    ...parentOptions,
    action
  };

  const { value: label } = useGetDataManCallbackOrTypeValue(
    action.label,
    actionOptions,
    "",
    [action.label]
  );
  const { value: icon } = useGetDataManCallbackOrTypeValue(
    action.icon,
    actionOptions,
    undefined,
    [action.icon]
  );
  const { value: isVisible } = useGetDataManCallbackOrTypeValue(
    action.isVisible,
    actionOptions,
    true,
    [action.isVisible]
  );
  const { value: isEnabled } = useGetDataManCallbackOrTypeValue(
    action.isEnabled,
    actionOptions,
    true,
    [action.isEnabled]
  );

  if (isVisible === false) {
    return null;
  }

  return (
    <DropdownMenuItem
      disabled={isEnabled === false}
      onClick={(event) => {
        event.stopPropagation();
        void onRun(action, actionOptions);
      }}
    >
      <span className="flex items-center gap-2">
        {icon ? <IconFromSet icon={icon} size={4} /> : null}
        {label}
      </span>
    </DropdownMenuItem>
  );
};

const BulkActionButton: React.FC<BulkActionButtonProps> = ({
  action,
  targetRecords
}) => {
  const dataMan = useDataMan();
  const isLoading = useBooleanState();

  const actionOptions: DataManActionCallbackOptions = {
    state: dataMan,
    action,
    targetRecords,
    target: targetRecords[0]
  };

  const { value: label } = useGetDataManCallbackOrTypeValue(
    action.label,
    actionOptions,
    "",
    [action.label]
  );
  const { value: icon } = useGetDataManCallbackOrTypeValue(
    action.icon,
    actionOptions,
    undefined,
    [action.icon]
  );
  // Visibility, enablement and sub-actions may depend on which records are
  // selected (e.g. only some of them qualify, or the people a given set of
  // records can be assigned to), so they re-resolve on selection.
  const { value: isVisible } = useGetDataManCallbackOrTypeValue(
    action.isVisible,
    actionOptions,
    true,
    [action.isVisible, targetRecords]
  );
  const { value: isEnabled } = useGetDataManCallbackOrTypeValue(
    action.isEnabled,
    actionOptions,
    true,
    [action.isEnabled, targetRecords]
  );
  const { value: subs } = useGetDataManCallbackOrTypeValue(
    action.subs,
    actionOptions,
    undefined,
    [action.subs, targetRecords]
  );

  if (isVisible === false) {
    return null;
  }

  const isDisabled = isEnabled === false;
  const isDeleteAction = action.id === "delete";
  const hasSubs = !!subs && subs.length > 0;

  const run = async (
    target: DataManAction,
    options: DataManActionCallbackOptions
  ) => {
    isLoading.true();
    await dataMan.triggerAction(target, options, (completed) => {
      isLoading.false();
      if (completed) {
        dataMan.clearSelection();
      }
    });
  };

  const onClick = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isDisabled) {
      return;
    }

    await run(action, actionOptions);
  };

  const button = (
    <Button
      isLoading={isLoading.state}
      variant={isDeleteAction ? "destructiveOutline" : "outline"}
      onClick={hasSubs ? (event) => event.stopPropagation() : onClick}
      disabled={isDisabled}
      size="sm"
      iconLeft={icon ? <IconFromSet icon={icon} size={4} /> : undefined}
      iconRight={hasSubs ? <ChevronDown className="h-4 w-4" /> : undefined}
    >
      {label}
    </Button>
  );

  if (!hasSubs) {
    return button;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={isDisabled}>
        {button}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {subs.map((subAction) => (
          <BulkSubActionItem
            key={subAction.id}
            action={subAction}
            parentOptions={actionOptions}
            onRun={run}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const DataManBulkActionsFooter: React.FC = () => {
  const { selectedItems, actions, data, dataIdKey, clearSelection, fullSize } =
    useDataMan();

  const selectedCount = selectedItems.length;
  const idKey = dataIdKey || "id";

  // Filter actions that allow bulk operations
  const bulkActions = React.useMemo(() => {
    return (actions || []).filter((action) => action.allowBulk);
  }, [actions]);

  // Get the selected records
  const targetRecords = React.useMemo(() => {
    if (!data) return [];
    return data.filter((item: Record<string, unknown>) =>
      selectedItems.includes(String(item[idKey]))
    );
  }, [data, selectedItems, idKey]);

  // Only show footer when there are selected items and fullSize mode is enabled
  if (selectedCount === 0 || !fullSize) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between px-4 py-3",
        "bg-white border-t border-neutral-200",
        "animate-in slide-in-from-bottom-2 duration-200"
      )}
    >
      <div className="flex items-center gap-4">
        <span className="text-sm font-medium text-neutral-700">
          {selectedCount} {selectedCount === 1 ? "item" : "items"} selected
        </span>
        <button
          onClick={clearSelection}
          className="text-sm text-neutral-500 hover:text-neutral-700 flex items-center gap-1"
        >
          <X className="h-4 w-4" />
          Clear
        </button>
      </div>

      {bulkActions.length > 0 && (
        <div className="flex items-center gap-2">
          {bulkActions.map((action) => (
            <BulkActionButton
              key={action.id}
              action={action}
              targetRecords={targetRecords}
            />
          ))}
        </div>
      )}
    </div>
  );
};
